import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { format, differenceInMinutes } from "date-fns";
import { cn } from "@/lib/utils";
import { OrderWithRelations } from "./types";
import { User, Truck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getOrderCardClass } from "./orderCardUtils";
import { OrderStatusBadge } from "@/components/ui/status-badge";

interface DraggableAssetOrderCardProps {
  order: OrderWithRelations;
  weekStart: Date;
  totalMinutes: number;
  onClick?: () => void;
}

export function DraggableAssetOrderCard({
  order,
  weekStart,
  totalMinutes,
  onClick,
}: DraggableAssetOrderCardProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `order-${order.id}`,
    data: {
      type: "order",
      orderId: order.id,
      order,
    },
  });

  const startTime = new Date(order.ready_time);
  const endTime = new Date(order.requested_delivery_time_end ?? order.requested_delivery_time);

  // Calculate position and width as percentages
  const startMinutes = differenceInMinutes(startTime, weekStart);
  const durationMinutes = Math.max(differenceInMinutes(endTime, startTime), 60);

  const leftPercent = Math.max(0, (startMinutes / totalMinutes) * 100);
  const widthPercent = Math.min((durationMinutes / totalMinutes) * 100, 100 - leftPercent);

  const showMissingIcons = order.status !== "canceled" && order.status !== "delivered";

  const handleClick = (e: React.MouseEvent) => {
    // Close tooltip immediately before triggering onClick
    setTooltipOpen(false);
    if (!isDragging) {
      onClick?.();
    }
  };

  return (
    <TooltipProvider>
      <Tooltip 
        delayDuration={200} 
        open={tooltipOpen && !isDragging}
        onOpenChange={setTooltipOpen}
      >
        <TooltipTrigger asChild>
          <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            onClick={handleClick}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 h-[calc(100%-16px)] rounded cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-primary/50 transition-shadow flex items-center justify-center gap-0.5 px-0.5",
              getOrderCardClass(order.status),
              isDragging && "opacity-50 ring-2 ring-primary"
            )}
            style={{
              left: `${leftPercent}%`,
              width: `${Math.max(widthPercent, 0.3)}%`,
              minWidth: "4px",
            }}
          >
            {showMissingIcons && (
              <>
                {!order.assigned_tractor_id && (
                  <Truck className="h-3 w-3 text-destructive shrink-0" />
                )}
                {!order.assigned_operator_id && (
                  <User className="h-3 w-3 text-destructive shrink-0" />
                )}
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-1.5 text-xs">
            <div className="font-semibold flex items-center gap-1.5">
              {order.unique_id}
              <OrderStatusBadge status={order.status} />
            </div>
            <div className="text-muted-foreground">
              {order.ship_from_city} → {order.ship_to_city}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1 border-t">
              <div>
                <span className="text-muted-foreground">Ready:</span>{" "}
                {format(startTime, "MMM d, HH:mm")}
              </div>
              <div>
                <span className="text-muted-foreground">Delivery:</span>{" "}
                {format(endTime, "MMM d, HH:mm")}
              </div>
            </div>
            {order.assigned_operator && (
              <div className="pt-1 border-t">
                <div className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span>{order.assigned_operator.name}</span>
                </div>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
